// Import the plugin types - this makes CdvPurchase namespace available
import { isNativePlatform, shouldUseIAP } from '@city-sage/aurum-avis-labs-components';
import 'cordova-plugin-purchase';

/**
 * IAP Product IDs - must match App Store Connect product IDs
 */
export const IAP_PRODUCT_IDS = {
  starter: 'com.citysage.mobile.credits.starter',
  standard: 'com.citysage.mobile.credits.standard',
  pro: 'com.citysage.mobile.credits.pro',
} as const;

export type IAPProductId = keyof typeof IAP_PRODUCT_IDS;

/**
 * Purchase result from IAP
 * Extends the plugin's VerifiedPurchase with receipt data needed for backend validation
 */
export type IAPPurchaseResult = CdvPurchase.VerifiedPurchase & {
  /** Base64 encoded receipt (appStoreReceipt for iOS) - required for backend validation */
  receipt: string;
};

/**
 * Service for handling Apple In-App Purchases using cordova-plugin-purchase
 * Based on official Capacitor guide: https://capacitorjs.com/docs/guides/in-app-purchases
 */
export class IAPService {
  private static instance: IAPService;
  private isInitialized = false;
  private store: typeof CdvPurchase.store | undefined;
  /**
   * Map of pending purchase promises
   * 
   * This is needed to bridge the plugin's callback-based API (when().approved().verified())
   * to our Promise-based API (purchaseProduct() returns Promise).
   * 
   * The plugin uses event-driven callbacks, but we expose a Promise API for easier
   * integration with React Query and async/await patterns.
   */
  private purchaseResolvers: Map<string, {
    resolve: (value: IAPPurchaseResult) => void;
    reject: (error: Error) => void;
  }> = new Map();

  private constructor() {
    // Access the global store object from cordova-plugin-purchase
    if (typeof CdvPurchase !== 'undefined' && CdvPurchase.store) {
      this.store = CdvPurchase.store;
    }
  }

  static getInstance(): IAPService {
    if (!IAPService.instance) {
      IAPService.instance = new IAPService();
    }
    return IAPService.instance;
  }

  /**
   * Check if IAP is available on this platform
   */
  isAvailable(): boolean {
    return shouldUseIAP() && isNativePlatform() && 
           typeof CdvPurchase !== 'undefined' && !!CdvPurchase.store;
  }

  /**
   * Initialize the IAP service
   * This should be called when the app starts
   */
  async initialize(): Promise<void> {
    if (!this.isAvailable()) {
      console.log('[IAP] Not available on this platform');
      return;
    }

    if (this.isInitialized) {
      return;
    }

    if (!this.store) {
      throw new Error('Store plugin not available. Make sure cordova-plugin-purchase is installed d npx cap sync has been run.');
    }

    try {
      console.log('[IAP] Initializing...');
      
      // Set verbosity for debugging (optional)
      if (import.meta.env.DEV) {
        this.store.verbosity = CdvPurchase.LogLevel.DEBUG;
      }

      // Register all our products as consumables (credits can be purchased multiple times)
      // For iOS, we need to specify the platform
      const productsToRegister: CdvPurchase.IRegisterProduct[] = Object.values(IAP_PRODUCT_IDS).map((productId) => ({
        id: productId,
        platform: CdvPurchase.Platform.APPLE_APPSTORE,
        type: CdvPurchase.ProductType.CONSUMABLE,
      }));
      
      this.store.register(productsToRegister);

      // Set up event listeners
      this.setupListeners();

      // Initialize the store for iOS platform
      const errors = await this.store.initialize([CdvPurchase.Platform.APPLE_APPSTORE]);
      if (errors.length > 0) {
        console.warn('[IAP] Initialization errors:', errors);
      }

      // Wait for store to be ready
      await new Promise<void>((resolve, reject) => {
        if (!this.store) {
          reject(new Error('Store not available'));
          return;
        }

        const timeout = setTimeout(() => {
          reject(new Error('Store initialization timeout'));
        }, 10000);

        this.store.ready(() => {
          clearTimeout(timeout);
          console.log('[IAP] Store ready');
          this.isInitialized = true;
          resolve();
        });

        // Also handle errors
        this.store.error((error: CdvPurchase.IError) => {
          if (error.code !== CdvPurchase.ErrorCode.SETUP) {
            // Only reject on non-setup errors during initialization
            return;
          }
          clearTimeout(timeout);
          console.error('[IAP] Store error:', error);
          reject(new Error(`Store error: ${error.message} (code: ${error.code})`));
        });
      });

      console.log('[IAP] Initialized successfully');
    } catch (error) {
      console.error('[IAP] Failed to initialize:', error);
      throw error;
    }
  }

  /**
   * Set up event listeners for purchase events
   */
  private setupListeners(): void {
    if (!this.store) return;

    // Listen to all transactions
    this.store.when()
      .approved(async (transaction: CdvPurchase.Transaction) => {
        console.log('[IAP] Transaction approved:', transaction.transactionId);
        
        // Get the product ID from the transaction
        const productId = transaction.products[0]?.id;
        if (!productId) {
          console.error('[IAP] No product ID in transaction');
          return;
        }
        
        // Verify the purchase
        try {
          await transaction.verify();
        } catch (error) {
          console.error('[IAP] Verification failed:', error);
          // Resolve the purchase promise with error
          const resolver = this.purchaseResolvers.get(productId);
          if (resolver) {
            resolver.reject(new Error('Purchase verification failed'));
            this.purchaseResolvers.delete(productId);
          }
        }
      })
      .verified((receipt: CdvPurchase.VerifiedReceipt) => {
        console.log('[IAP] Receipt verified');
        
        // Get the first purchase from the receipt
        const purchase = receipt.collection[0];
        if (!purchase) {
          console.error('[IAP] No purchase found in verified receipt');
          return;
        }
        
        // Resolve the purchase promise
        const resolver = this.purchaseResolvers.get(purchase.id);
        if (resolver) {
          // Get the receipt data from the source receipt
          // For iOS, this is the appStoreReceipt (base64 encoded)
          const sourceReceipt = receipt.sourceReceipt;
          let receiptData = '';
          
          // For iOS, get the appStoreReceipt from the adapter
          if (sourceReceipt.platform === CdvPurchase.Platform.APPLE_APPSTORE) {
            const adapter = this.store?.getAdapter(CdvPurchase.Platform.APPLE_APPSTORE) as CdvPurchase.AppleAppStore.Adapter | undefined;
            if (adapter) {
              // The adapter has a _receipt property that is the SKApplicationReceipt
              // Access it through the receipts getter
              const appReceipts = adapter.receipts.filter(r => r.platform === CdvPurchase.Platform.APPLE_APPSTORE);
              const appReceipt = appReceipts[0] as CdvPurchase.AppleAppStore.SKApplicationReceipt | undefined;
              if (appReceipt && 'nativeData' in appReceipt) {
                receiptData = appReceipt.nativeData.appStoreReceipt;
              }
            }
          }
          
          // Fallback: try to get from sourceReceipt if it has nativeData
          if (!receiptData && 'nativeData' in sourceReceipt) {
            const skReceipt = sourceReceipt as CdvPurchase.AppleAppStore.SKApplicationReceipt;
            receiptData = skReceipt.nativeData?.appStoreReceipt || '';
          }
          
          // Combine VerifiedPurchase with receipt data
          const result: IAPPurchaseResult = {
            ...purchase,
            receipt: receiptData,
            // Ensure required fields are present
            transactionId: purchase.transactionId || '',
            id: purchase.id, // productId
          };
          
          resolver.resolve(result);
          this.purchaseResolvers.delete(purchase.id);
          
          // Finish the receipt (mark as consumed for consumables)
          receipt.finish();
        }
      });
  }

  /**
   * Load available products from the App Store
   * Returns the plugin's Product objects directly for type safety
   */
  async loadProducts(productIds: string[]): Promise<CdvPurchase.Product[]> {
    if (!this.isAvailable()) {
      throw new Error('IAP is not available on this platform');
    }

    if (!this.isInitialized) {
      await this.initialize();
    }

    if (!this.store) {
      throw new Error('Store not available');
    }

    try {
      console.log('[IAP] Loading products:', productIds);
      
      // Update to ensure we have latest product info
      await this.store.update();
      
      // Get products from store - return plugin's Product type directly
      return this.store.products.filter(p => productIds.includes(p.id));
    } catch (error) {
      console.error('[IAP] Failed to load products:', error);
      throw error;
    }
  }

  /**
   * Purchase a product
   */
  async purchaseProduct(productId: string): Promise<IAPPurchaseResult> {
    if (!this.isAvailable()) {
      throw new Error('IAP is not available on this platform');
    }

    if (!this.isInitialized) {
      await this.initialize();
    }

    if (!this.store) {
      throw new Error('Store not available');
    }

    try {
      console.log('[IAP] Purchasing product:', productId);
      
      // Find the product
      const product = this.store.get(productId, CdvPurchase.Platform.APPLE_APPSTORE);
      if (!product) {
        throw new Error(`Product ${productId} not found. Make sure it's registered and the store has been updated.`);
      }

      if (!product.canPurchase) {
        throw new Error(`Product ${productId} cannot be purchased. Check App Store Connect configuration.`);
      }

      // Get the default offer for the product
      const offer = product.getOffer();
      if (!offer) {
        throw new Error(`No offer available for product ${productId}`);
      }

      // Create a promise that will be resolved when the purchase is verified
      const purchasePromise = new Promise<IAPPurchaseResult>((resolve, reject) => {
        this.purchaseResolvers.set(productId, { resolve, reject });
        
        // Set a timeout
        setTimeout(() => {
          if (this.purchaseResolvers.has(productId)) {
            this.purchaseResolvers.delete(productId);
            reject(new Error('Purchase timeout'));
          }
        }, 60000); // 60 second timeout
      });

      // Initiate the purchase
      const error = await this.store.order(offer);
      if (error) {
        this.purchaseResolvers.delete(productId);
        throw new Error(`Purchase failed: ${error.message}`);
      }
      
      // Wait for the purchase to complete
      return await purchasePromise;
    } catch (error) {
      // Clean up resolver on error
      this.purchaseResolvers.delete(productId);
      console.error('[IAP] Purchase failed:', error);
      throw error;
    }
  }

  /**
   * Restore previous purchases
   */
  async restorePurchases(): Promise<IAPPurchaseResult[]> {
    if (!this.isAvailable()) {
      throw new Error('IAP is not available on this platform');
    }

    if (!this.isInitialized) {
      await this.initialize();
    }

    if (!this.store) {
      throw new Error('Store not available');
    }

    try {
      console.log('[IAP] Restoring purchases...');
      
      // Update the store to restore purchases
      await this.store.update();
      
      // Get all owned products
      const ownedProducts = this.store.products.filter(p => 
        this.store!.owned(p.id)
      );
      
      // Get verified receipts for owned products
      const verifiedReceipts = this.store.verifiedReceipts;
      
      return verifiedReceipts.flatMap(receipt => 
        receipt.collection
          .filter(p => ownedProducts.some(op => op.id === p.id))
          .map(p => {
            // Get receipt data from source receipt
            let receiptData = '';
            const sourceReceipt = receipt.sourceReceipt;
            
            if (sourceReceipt.platform === CdvPurchase.Platform.APPLE_APPSTORE && 'nativeData' in sourceReceipt) {
              const skReceipt = sourceReceipt as CdvPurchase.AppleAppStore.SKApplicationReceipt;
              receiptData = skReceipt.nativeData?.appStoreReceipt || '';
            }
            
            // Combine VerifiedPurchase with receipt data
            return {
              ...p,
              receipt: receiptData,
              // Ensure required fields are present
              transactionId: p.transactionId || '',
              id: p.id, // productId
            };
          })
      );
    } catch (error) {
      console.error('[IAP] Failed to restore purchases:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const iapService = IAPService.getInstance();
